import { HttpClient, HttpErrorResponse, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { HTTP } from '@ionic-native/http';
import { Network } from '@ionic-native/network';
import { Platform } from 'ionic-angular';

import { APP_CONFIG } from '../../app.config';
import { HttpHelper } from '../helpers/http.helper';
import { Error } from '../models/error.model';
import { UserService } from './user.service';

@Injectable()
export class RequestService {

	private header: any;
	private userService: UserService;
	private token: string;

	private httpHelper: HttpHelper;

	constructor(
		private httpClient: HttpClient,
		private httpNative: HTTP,
		private network: Network,
		private platform: Platform
	) {
		this.platform.ready().then(() => {
			this.httpHelper = new HttpHelper(platform, httpClient, httpNative);
		});
	}

	//Para contornar erros de circular dependency
	setUserService(service: UserService): void {
		this.userService = service;
	}

	setAuthToken(token: string): void {
		this.token = token;
	}

	getAuthToken(): string {
		return this.token;
	}

	getData(url: string, forceUseHttpClient?: boolean): Promise<any> {
		return new Promise((resolve, reject) => {
			this.checkConnection().then(() => {
				const getRequest: VoidFunction = () => {
					this.httpHelper.get(url, this.getRequestOptions(url), forceUseHttpClient).then((resp) => {
						this.header = null;
						resolve(this.getJsonData(resp));
					}, (error) => {
						this.catchError(error, url, getRequest, reject);
					}).catch((error) => {
						this.catchError(error, url, getRequest, reject);
					});
				};
				getRequest();
			}, (error) => {
				reject(error);
			});
		});
	}

	putData(url: string, data?: any): Promise<any> {
		return new Promise((resolve, reject) => {
			this.checkConnection().then(() => {
				const putRequest: VoidFunction = () => {
					this.httpHelper.put(url, data || { }, this.getRequestOptions(url)).then((resp) => {
						this.header = null;
						resolve(this.getJsonData(resp));
					}, (error) => {
						this.catchError(error, url, putRequest, reject);
					}).catch((error) => {
						this.catchError(error, url, putRequest, reject);
					});
				};
				putRequest();
			}, (error) => {
				reject(error);
			});
		});
	}

	patchData(url: string, data?: any, returnLocation?: boolean): Promise<any> {
		return new Promise((resolve, reject) => {
			this.checkConnection().then(() => {
				const patchRequest: VoidFunction = () => {
					this.httpHelper.patch(url, data || { }, this.getRequestOptions(url)).then((resp) => {
						this.header = null;
						if (returnLocation) {
							resolve({
								location: this.platform.is('cordova') ? resp.headers['location'] : resp.headers.get('Location'),
								data: this.getJsonData(resp.body || resp)
							});
						} else {
							resolve(this.getJsonData(resp.body || resp));
						}
					}, (error) => {
						this.catchError(error, url, patchRequest, reject);
					}).catch((error) => {
						this.catchError(error, url, patchRequest, reject);
					});
				};
				patchRequest();
			}, (error) => {
				reject(error);
			});
		});
	}

	postData(url: string, data?: any, returnLocation?: boolean): Promise<any> {
		return new Promise((resolve, reject) => {
			this.checkConnection().then(() => {
				const postRequest: VoidFunction = () => {
					this.httpHelper.post(url, data || { }, this.getRequestOptions(url)).then((resp) => {
						this.header = null;
						if (returnLocation) {
							resolve({
								location: this.platform.is('cordova') ? resp.headers['location'] : resp.headers.get('Location'),
								data: this.getJsonData(resp.body || resp)
							});
						} else {
							resolve(this.getJsonData(resp.body || resp));
						}
					}, (error) => {
						this.catchError(error, url, postRequest, reject);
					}).catch((error) => {
						this.catchError(error, url, postRequest, reject);
					});
				};
				postRequest();
			}, (error) => {
				reject(error);
			});
		});
	}

	deleteData(url: string): Promise<any> {
		return new Promise((resolve, reject) => {
			this.checkConnection().then(() => {
				const deleteRequest: VoidFunction = () => {
					this.httpHelper.delete(url, this.getRequestOptions(url)).then((resp) => {
						this.header = null;
						resolve(this.getJsonData(resp));
					}, (error) => {
						this.catchError(error, url, deleteRequest, reject);
					}).catch((error) => {
						this.catchError(error, url, deleteRequest, reject);
					});
				};
				deleteRequest();
			}, (error) => {
				reject(error);
			});
		});
	}

	/**
	 * Contrói a url com base nos parametros já definidos na url com {key}
	 */
	buildUrlPathParams(params: any, urlRequest: string): string {
		if (params == null)
			return null;

		for (const key in params) {
			if (params[key] != null) {
				urlRequest = urlRequest.replace("{" + key + "}", encodeURIComponent(params[key]));
			}
		}
		console.log("urlRequest: " + urlRequest);
		return urlRequest;
	}

	/**
	 * Contrói os parâmetros para query params
	 */
	buildUrlParams(params: any): string {
		if (params == null)
			return null;

		const urlParams: URLSearchParams = new URLSearchParams();
		for (const key in params) {
			if (params[key] != null) {
				urlParams.append(key, params[key]);
			}
		}
		return urlParams.toString();
	}

	/**
	 * Adiciona properties no header da requisição. Passar os campos chave-valor.
	 * Exemplo: key = 'Content-Type'; value = 'application/json'
	 */
	addParamsHeader(key: string, value: string): void {
		if (!this.header) {
			this.header = { };
		}
		if (this.header[key]) {
			delete this.header[key];
		}
		this.header[key] = value;
	}

	/* Private métodos
	================================*/

	private catchError(error: any, url: string, retryCallback: VoidFunction, reject: any): void {
		this.header = null;
		this.treatError(error, url).then((e: any) => {
			if (e.tryAgain) {
				retryCallback();
			} else {
				reject(e);
			}
		});
	}

	private checkConnection(): Promise<any> {
		console.log("Status rede: " + this.network.type);
		if (this.network.type === 'none') {
			return Promise.reject({
				status: APP_CONFIG.ERRORS.OFFLINE,
				message: "Você está offline."
			});
		}
		return Promise.resolve();
	}

	private getJsonData(response: any): any {
		let body: any = null;
		try {
			if (this.platform.is('cordova')) {
				if (response.data != null) {
					body = JSON.parse(response.data);
				} else {
					body = typeof response === 'string' ? JSON.parse(response) : response;
				}
			} else {
				body = response;
			}
		} catch (e) {
			//sem content no response
		}
		return body;
	}

	private treatError(error: HttpErrorResponse | any, urlRequest: string): Promise<Error> {
		return new Promise((resolve) => {
			console.error(JSON.stringify(error));
			const errorApp: Error = new Error(0, "ERRORS.GENERIC");

			if (error.status) {
				// 401 (Não autorizado - Usuario e ou senha inválido, token expirado)
				// 403 (Acesso negado ao acessar o recurso, scope inválido)
				switch (error.status) {
					case 500:
						errorApp.message = "ERRORS.SERVER_ERROR";
						break;

					case 503:
						errorApp.message = "ERRORS.SERVICE_UNAVAILABLE";
						break;

					case 504:
						errorApp.message = "ERRORS.TIMEOUT";
						break;

					case 403:
						errorApp.message = "ERRORS.ACCESS_DENIED";
						// Recarrego a página para evitar possíveis problemas de ter 2 abas com stores diferentes
						document.location.reload();
						return;

					default:
						break;
				}
				errorApp.status = error.status;
			}

			if (error && error.name && error.name === "TimeoutError") {
				errorApp.message = "ERRORS.TIMEOUT";
			} else {
				const body: any = this.getJsonData(error.error || error.data);

				if (body) {
					errorApp.fullError = body;
					if (body.errorMessage || body.message || body.errors) {
						errorApp.message = body.errorMessage || body.message || body.errors;
					}
				}
			}

			if (errorApp.status === 401 && !this.isAuthRequest(urlRequest) || (errorApp.status === 412 && errorApp.message === "USER.SHOULD_LOGIN_AGAIN")) {
				this.userService.refreshToken().then(() => {
					errorApp.tryAgain = true;
					resolve(errorApp);
				}, (tokenErrorResponse) => {
					resolve(tokenErrorResponse);
				});
			} else if (errorApp.status === 400 && this.isAuthRequest(urlRequest)) {
				//Springboot retorna 400 tbm quando o refresh token é inválido.
				//Para tratar tudo como 401 na controller, altero o status code para 401
				errorApp.status = 401;
				resolve(errorApp);
			} else {
				resolve(errorApp);
			}
		});
	}

	private sendLogError(error: Error, requestType: string, url: string, data?: any): void {
		// Só envia log de evento se for ambiente de produção
		if (url.indexOf("api.fastget.com.br") >= 0) {
			let logError: string = "";
			let service: string = url;
			try {
				const urlService: Array<string> = url.split("/api/");
				service = urlService.length > 0 ? urlService[1] : url;

				const params: string = data ? JSON.stringify(data) : null;
				logError = "Erro: " + JSON.stringify(error) + (params ? ("; Dados envio: " + params) : "");
			} catch (e) { }

			console.log("Erro", requestType + ": " + service, logError);
			// this.ga.trackEvent("Erro", requestType + ": " + service, logError);
		}
	}

	/**
	 * Retorna o cabeçalho com os parametros setados para a requisição.
	 */
	private getRequestOptions(urlRequest: string): any {
		if (!this.isAuthRequest(urlRequest) && !this.isExternalRequest(urlRequest)) {
			this.addUserTokenToHeader();
		}
		let options: any = null;

		if (this.header) {
			options = this.platform.is("cordova") ? this.header : new HttpHeaders(this.header);
		}
		return options;
	}

	private addUserTokenToHeader(): void {
		if (this.token) {
			this.addParamsHeader('Authorization', 'Bearer ' + this.token);
		}
	}

	private isAuthRequest(urlRequest: string): boolean {
		if (!urlRequest) return false;
		return urlRequest.indexOf(APP_CONFIG.WEBSERVICE.AUTH_TOKEN) !== -1;
	}

	private isExternalRequest(urlRequest: string): boolean {
		if (!urlRequest) return false;
		return urlRequest.indexOf("https://viacep.com.br/ws/") !== -1 || urlRequest.indexOf("/public/") !== -1;;
	}
}



// WEBPACK FOOTER //
// ./src/app/shared/services/request.service.ts